<script setup lang="ts">
import { ref, onUnmounted } from 'vue';
import { QrcodeStream, type DetectedBarcode } from 'vue-qrcode-reader';
import ComModal from './ComModal.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';

const emit = defineEmits<{
  (event: 'detect', serial: string): void;
  (event: 'cancel'): void;
}>();
const loading = ref(true);
const error = ref('');
let active = true;
onUnmounted(() => {
  active = false;
});

const onDetect = (codes: DetectedBarcode[]) => {
  if (!active) return;
  const value = codes[0]?.rawValue;
  if (typeof value !== 'string' || !value.trim()) return;
  active = false;
  emit('detect', value.trim());
};

const onError = (err: unknown) => {
  if (!active) return;
  const name = err && typeof err === 'object' && 'name' in err ? err.name : '';
  error.value =
    name === 'NotAllowedError'
      ? 'Camera access was denied. You can enter the inverter serial number manually.'
      : 'Unable to open the camera. You can enter the inverter serial number manually.';
  active = false;
  loading.value = false;
};

const cancel = () => {
  active = false;
  emit('cancel');
};
</script>

<template>
  <ComModal
    :visible="true"
    title="Scan inverter serial number"
    description="Scan a QR code containing the inverter serial number."
    data-tn="inverter-serial-scanner"
    @cancel="cancel"
    @update:visible="cancel"
  >
    <p v-if="error" role="alert" data-tn="inverter-scanner-error">{{ error }}</p>
    <QrcodeStream v-else @detect="onDetect" @error="onError" @camera-on="loading = false">
      <div v-if="loading">
        <LoadingSpinner />
        <p>Opening camera...</p>
      </div>
    </QrcodeStream>
    <template #modal-buttons>
      <ButtonPrimary html-type="button" data-tn="inverter-scanner-cancel" @click="cancel"
        >Cancel</ButtonPrimary
      >
    </template>
  </ComModal>
</template>
